import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import type { CncSpeciesAssignment, RfqProject } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { updateCncInviteSpeciesAssignments } from '../../utils/cncInviteInputEdit';
import './LegacyQuoteEditModal.css';

export type CncSupplierInputEditModalProps = {
  open: boolean;
  onClose: () => void;
  project: RfqProject;
  orgId: string;
  orgName: string;
  assignments: CncSpeciesAssignment[];
  onUpdateProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  onSuccess?: () => void;
};

const CncSupplierInputEditModal: React.FC<CncSupplierInputEditModalProps> = ({
  open,
  onClose,
  project,
  orgId,
  orgName,
  assignments,
  onUpdateProject,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [rows, setRows] = useState<CncSpeciesAssignment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRows(assignments.map((a) => ({ ...a })));
    setError(null);
  }, [open, assignments]);

  if (!open) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be signed in.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateCncInviteSpeciesAssignments(
        onUpdateProject,
        project,
        orgId,
        rows.map((r) => ({
          ...r,
          boardFeet:
            r.boardFeet != null && Number.isFinite(r.boardFeet) && r.boardFeet > 0
              ? Math.round(r.boardFeet)
              : undefined,
        })),
        { uid: user.id, name: user.name || user.email || 'Staff' },
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save CNC input.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lqi-modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className="lqi-modal lqi-modal--timber"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cnc-input-edit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lqi-modal-head">
          <div>
            <h2 id="cnc-input-edit-title">Edit CNC quote request input</h2>
            <p className="lqi-modal-sub">
              Update board feet and notes sent to {orgName}. Suppliers see the published package; this adjusts what we asked them to quote.
            </p>
          </div>
          <button type="button" className="lqi-modal-close" onClick={handleClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <form className="lqi-modal-body" onSubmit={(ev) => void handleSubmit(ev)}>
          {rows.map((row, idx) => (
            <div key={row.species} className="lqi-species-quote-line">
              <label className="lqi-field lqi-field--inline">
                <span>{row.species} — board feet</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  disabled={busy}
                  value={row.boardFeet ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : Number(e.target.value);
                    setRows((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, boardFeet: v } : r)),
                    );
                  }}
                />
              </label>
              <label className="lqi-field lqi-field--inline">
                <span>Note</span>
                <input
                  type="text"
                  disabled={busy}
                  value={row.note ?? ''}
                  onChange={(e) => {
                    const note = e.target.value;
                    setRows((prev) =>
                      prev.map((r, i) => (i === idx ? { ...r, note: note || undefined } : r)),
                    );
                  }}
                />
              </label>
            </div>
          ))}
          {error && <p className="lqi-error">{error}</p>}
          <div className="lqi-modal-actions">
            <button type="button" className="lqi-btn lqi-btn--ghost" disabled={busy} onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="lqi-btn lqi-btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CncSupplierInputEditModal;
